// apps/widget/src/components/split-visual.tsx
//
// The right half of a split screen: the clinic's photograph when they have set
// one, otherwise a rendering of the product's own output in their brand colour.
//
// Shared rather than owned by the welcome screen, because every split screen
// shows the same thing and a second copy would drift from the first.

import { METRICS } from '@skinvizo/shared';
import { t, metricLabel } from '../lib/i18n';
import type { WidgetConfig } from '../lib/api';

export function SplitVisual({ config }: { config: WidgetConfig }) {
  return (
    <div className="gs-welcome__visual" aria-hidden="true">
      {config.heroImageUrl ? (
        <>
          <img src={config.heroImageUrl} alt="" className="gs-welcome__img" />
          {/* A scrim, or the scores become unreadable over a light photo. */}
          <span className="gs-welcome__scrim" />
          <ExampleScores onPhoto />
        </>
      ) : (
        <ResultPreview />
      )}
    </div>
  );
}

/**
 * The sample numbers. Rendered over the clinic's photograph when they have set
 * one, and over the fallback rendering when they have not — `onPhoto` only
 * switches the colours to the ones that survive a dark scrim.
 */
export function ExampleScores({ onPhoto }: { onPhoto?: boolean }) {
  return (
    <div className={onPhoto ? 'gs-welcome__scores-block gs-welcome__scores-block--photo' : 'gs-welcome__scores-block'}>
      <p className="gs-welcome__preview-note">{t('report.example')}</p>

      <div className="gs-welcome__scores">
        <span>
          <span className="gs-welcome__score-label">{t('report.skinScore')}</span>
          <br />
          <span className="gs-welcome__score-value">89</span>
        </span>
        <span>
          <span className="gs-welcome__score-label">{t('report.skinAge')}</span>
          <br />
          <span className="gs-welcome__score-value">30</span>
        </span>
      </div>

      <div className="gs-welcome__rings">
        {/* Names come from the shared metric catalogue, the same source the
            real report renders from, so they cannot drift. */}
        {(
          [
            ['spots', 86],
            ['oiliness', 76],
            ['acne', 95],
            ['pores', 79],
          ] as const
        ).map(([key, value]) => (
          <span key={key} className="gs-welcome__ring">
            <b>{value}</b>
            {metricLabel(key, METRICS[key].label)}
          </span>
        ))}
      </div>
    </div>
  );
}

function ResultPreview() {
  return (
    <div className="gs-welcome__preview">
      <svg
        className="gs-welcome__mesh"
        viewBox="0 0 240 300"
        preserveAspectRatio="xMidYMid slice"
      >
        <defs>
          <radialGradient id="gsFade" cx="50%" cy="42%" r="58%">
            <stop offset="0%" stopColor="#fff" stopOpacity="1" />
            <stop offset="100%" stopColor="#fff" stopOpacity="0" />
          </radialGradient>
          <mask id="gsMask">
            <rect width="240" height="300" fill="url(#gsFade)" />
          </mask>
          {/* Without an envelope the longitude lines bulge outward and the
              whole thing reads as a barrel rather than a face. */}
          <clipPath id="gsFace">
            <ellipse cx="120" cy="146" rx="60" ry="90" />
          </clipPath>
        </defs>

        <g clipPath="url(#gsFace)" mask="url(#gsMask)" fill="none" stroke="currentColor" strokeWidth="0.7">
          {/* Longitude lines, narrowing towards the centre like a face mesh. */}
          {[-56, -40, -25, -12, 0, 12, 25, 40, 56].map((dx) => (
            <path
              key={dx}
              d={`M${120 + dx * 0.55} 58 C ${120 + dx} 110, ${120 + dx} 175, ${120 + dx * 0.62} 232`}
            />
          ))}
          {/* Latitude lines. */}
          {[70, 92, 114, 136, 158, 180, 202, 224].map((y, i) => (
            <path
              key={y}
              d={`M${68 + Math.abs(i - 3.5) * 3.4} ${y} Q 120 ${y + 9} ${172 - Math.abs(i - 3.5) * 3.4} ${y}`}
            />
          ))}
        </g>

        <ellipse
          cx="120"
          cy="146"
          rx="60"
          ry="90"
          fill="none"
          stroke="currentColor"
          strokeWidth="0.9"
          mask="url(#gsMask)"
        />

        <g mask="url(#gsMask)" fill="currentColor">
          {[
            [100, 128],
            [140, 128],
          ].map(([cx, cy]) => (
            <circle key={cx} cx={cx} cy={cy} r="2.4" />
          ))}
        </g>
      </svg>

      <ExampleScores />
    </div>
  );
}
